import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiLogin } from "../../api/auth";
import { useAuth } from "../../context/AuthContext";

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const { setAuth } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError("Email and password are required"); return; }
    if (!email.endsWith("@capgemini.com")) {
      setError("Only Capgemini email addresses are accepted (e.g. example@capgemini.com)");
      return;
    }

    setLoading(true);
    try {
      const { token, user } = await apiLogin(email, password);
      setAuth(token, user);

      const role = user.roleName?.toLowerCase?.() ?? "";
      if (role === "user") {
        navigate("/user-dashboard");
      } else if (role === "mentor") {
        navigate("/mentor-dashboard");
      } else if (role === "admin") {
        navigate("/admin-dashboard");
      } else if (role === "jury") {
        navigate("/jury-dashboard");
      } else {
        setError("Access denied: unrecognized role.");
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="
flex flex-col justify-center
  w-full lg:w-1/2
  px-4 sm:px-6 lg:px-16
  py-10 lg:py-20
">
      <h2 className="text-3xl font-light mb-1">Log in</h2>

      <p className="text-sm text-gray-600 mb-6">
        Don't have an account yet?{" "}
        <a href="/signup" className="text-[#1D365A] hover:underline">Create an account</a>
      </p>

      {error && <p className="text-red-500 text-sm w-full">{error}</p>}

      <form className="space-y-6 w-full" onSubmit={handleSubmit}>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-normal">Email*</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="box-border px-3 py-2 w-[544px] h-[40px] bg-[#F8FAFC] border-b-2 border-[#1D365A] focus:outline-none focus:border-blue-500"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-normal">Password*</label>
          <div className="relative w-full">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="box-border px-3 py-2 w-[544px] h-[40px] bg-[#F8FAFC] border-b-2 border-[#1D365A] focus:outline-none focus:border-blue-500"
            />
            <span onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-3 cursor-pointer">
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 text-gray-600">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.964 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 text-gray-600">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.5a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              )}
            </span>
          </div>
        </div>

        <div className="flex justify-between items-center mt-2">
          <label className="flex items-center text-sm text-gray-600">
            <input type="checkbox" className="mr-2" /> Remember me
          </label>
          <a href="#" className="text-xs text-[#1D365A] hover:underline">Forgot password?</a>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="flex flex-row justify-center items-center py-2 px-6 gap-2 w-[476px] h-12 bg-[#1D365A] rounded-full text-white cursor-pointer hover:bg-[#162a47] transition disabled:opacity-60"
        >
          {loading ? "Logging in..." : "Login"}
        </button>
      </form>
    </div>
  );
};

export default LoginForm;
